/**
 * MIGRATIONS
 *
 * Migrations are the functions that move the data from an old data schema
 * to a new one.
 *
 * They are applied automatically to every old model that is loaded in a
 * newer version.
 */

/**
 * @callback MigrationFunction
 * @param {object} data
 * @param {object} options
 * @param {object} options.rootState
 * @param {object} options.oldData
 * @param {string | null} options.versionBefore
 * @returns {object} migrated data
 */

/**
 * @typedef Migration
 * @property {string} applyToVersions version string (all models saved with a version satisfied by the string will be affected) e.g. "<1.33.7"
 * @property {string} type the migration type (before|after)
 * @property {string} name
 * @property {MigrationFunction} migrate
 */

import satisfies from "semver/functions/satisfies";
import compare from "semver/functions/compare";

// load all migrations in the directory and save them as one array:
const modules = import.meta.glob("./*.(js|ts)");
/** @type {Migration[]} */
const migrations = [];
for (const path in modules) {
  const fileName = path.substring(2, path.length - 3);
  modules[path]().then((mod) => {
    if (mod.default) {
      mod.default.fileName = fileName;
      migrations.push(mod.default);
    }
  });
}

/**
 * @param {string | null} dataVersion version the data was saved with
 * @param {"before"|"after"} type — the migration type (before/after loading, before/after item)
 */
export function getMigrations(type, dataVersion = null) {
  let migrationsToApply = migrations.filter(
    (migration) => migration != null && migration.type == type
  );

  if (dataVersion != null) {
    migrationsToApply = migrationsToApply.filter((migration) => {
      const satisfied = satisfies(dataVersion, migration.applyToVersions, {
        includePrerelease: true
      });
      return satisfied;
    });
  }

  migrationsToApply.sort(sortAscendingByVersions);

  console.log("migrationsToApply", migrationsToApply);

  /**
   * Migrate the data by applying all fitting migrations to it
   * @param {object} data
   * @param {object} options
   * @param {object} options.rootState
   * @param {object} options.oldData
   * @param {string | null} options.versionBefore
   * @param {string} [options.moduleName]
   */
  const applyMigrations = function (data, options) {
    return migrationsToApply.reduce((data, migration) => {
      // console.log(
      //   "migrate",
      //   migration.name,
      //   migration.applyToVersions,
      //   JSON.parse(JSON.stringify(data))
      // );
      return migration.migrate(data, options);
    }, data);
  };
  return applyMigrations;
}

function sortAscendingByVersions(a, b) {
  const versionA = a.applyToVersions.replace(/[^.0-9]+/, "");
  const versionB = b.applyToVersions.replace(/[^.0-9]+/, "");
  const diff = compare(versionA, versionB);
  if (diff != 0) return diff;
  return a.fileName.localeCompare(b.fileName);
}

export { migrations };
